// src/utils/clientIdentity.js
//
// Pass 63 — CANONICAL CLIENT IDENTITY RESOLUTION (pure, source-neutral, NO Firestore).
//
// One real customer → one stable clientId across cases / bookings / seasons / future source
// systems / future locations. This module is the single identity contract that current workflows
// AND future source adapters (Pass 64) call. It knows nothing about Google rows, RoomBoss,
// SAS workbook layout, or any operator — source-specific ids only ever enter as *evidence*.
//
// HARD RULES (enterprise-critical):
//   • NAME ALONE IS NEVER SUFFICIENT for an automatic client match/merge. No fuzzy names, no
//     Levenshtein, no "same surname + same dates", no AI identity guesses.
//   • A stable clientId is opaque + organization-scoped + permanent. It is NOT a Firebase Auth
//     UID, NOT an email/phone/name string, and must not change when contact details change.
//   • Shared / conflicting contact evidence FAILS CLOSED (ambiguous / conflict — never auto-merge).

const str = (v) => String(v == null ? '' : v).trim();

// ── confidence + evidence taxonomy ─────────────────────────────────────────────
export const IDENTITY_CONFIDENCE = { EXPLICIT: 'explicit', STRONG: 'strong', CONTACT: 'contact', WEAK: 'weak' };
export const EVIDENCE_TYPE = {
  CLIENT_ID: 'clientId',                 // an already-canonical business id (EXPLICIT)
  SOURCE_CUSTOMER_ID: 'sourceCustomerId', // a trusted external-system customer id (EXPLICIT)
  USER_ID: 'requesterUserId',            // an authenticated Motus account link (STRONG)
  EMAIL: 'email',                        // exact normalized email (CONTACT — may be shared)
  PHONE: 'phone',                        // exact normalized phone (CONTACT — may be shared)
  NAME: 'name',                          // display/search only (WEAK — never strong identity)
};
export const RESOLUTION_STATUS = { MATCHED: 'MATCHED', NEW: 'NEW', AMBIGUOUS: 'AMBIGUOUS', CONFLICT: 'CONFLICT', UNRESOLVED: 'UNRESOLVED' };

// ── centralized normalization (the ONE place — clientsWorkspace + wizard + future adapters share it) ──
export const normalizeClientEmail = (v) => str(v).toLowerCase();
// Conservative phone normalization: keep digits and a single leading '+'. We DO NOT fabricate an
// E.164 country code we cannot safely infer — a conservative representation is the identity value.
export const normalizeClientPhone = (v) => {
  const s = str(v).replace(/[^\d+]/g, '');
  if (!s) return '';
  const plus = s.startsWith('+');
  const digits = s.replace(/\+/g, '');
  return (plus ? '+' : '') + digits;
};
// Name is display/search evidence only; normalized purely to dedupe display, never for auto-merge.
export const normalizeClientName = (v) => str(v).toLowerCase().replace(/\s+/g, ' ');

// ── deterministic opaque stable id ─────────────────────────────────────────────
// FNV-1a (two salted rounds → 16 hex chars). Pure, self-contained (browser + node + tests), no
// crypto import. This is an opaque *stable identifier*, NOT a security primitive: reproducibility
// (same org + same evidence key → same id) is the property that makes one client survive seasons.
function fnv1a(s) { let h = 0x811c9dc5 >>> 0; for (let i = 0; i < s.length; i += 1) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; } return h >>> 0; }
function stableHashHex(s) { const a = fnv1a(s); const b = fnv1a(`${s}pass63`); return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0'); }
// A stable clientId anchored to organization + a strong/contact identity key. Never contains raw PII
// (the key is one-way hashed) and never depends on a mutable name.
export function stableClientId(organizationId, identityKey) { return `client_${stableHashHex(`${str(organizationId)}::${str(identityKey)}`)}`; }

// Accelerator C — a SOURCE-NAMESPACED external customer identity key. Two independent sources that happen to reuse
// the same raw external customer id (e.g. both "CUS-9") must NOT collide within one Motus org. The namespace is
// SERVER-controlled (from trusted integration config), never client/body-supplied; two integrations may deliberately
// SHARE a namespace to unify a genuinely shared upstream customer domain. The raw id is preserved as provenance.
export function sourceCustomerIdentityKey(namespace, externalCustomerId) {
  const ns = str(namespace);
  const ext = str(externalCustomerId);
  if (!ext) return '';
  return ns ? `${ns}::${ext}` : ext;
}

// ── evidence extraction (source-neutral) ────────────────────────────────────────
export function normalizeClientEvidence(record = {}) {
  const clientId = str(record.clientId);
  const sourceCustomerId = str(record.sourceCustomerId || record.externalCustomerId);
  const requesterUserId = str(record.requesterUserId);
  const email = normalizeClientEmail(record.requesterEmailNormalized || record.requesterEmail || record.leadGuest?.email);
  const phone = normalizeClientPhone(record.requesterPhone || record.leadGuest?.phone);
  const name = normalizeClientName(record.requesterName || record.leadGuest?.fullName);
  const evidence = [];
  if (clientId) evidence.push({ type: EVIDENCE_TYPE.CLIENT_ID, value: clientId, tier: IDENTITY_CONFIDENCE.EXPLICIT });
  if (sourceCustomerId) evidence.push({ type: EVIDENCE_TYPE.SOURCE_CUSTOMER_ID, value: sourceCustomerId, tier: IDENTITY_CONFIDENCE.EXPLICIT });
  if (requesterUserId) evidence.push({ type: EVIDENCE_TYPE.USER_ID, value: requesterUserId, tier: IDENTITY_CONFIDENCE.STRONG });
  if (email) evidence.push({ type: EVIDENCE_TYPE.EMAIL, value: email, tier: IDENTITY_CONFIDENCE.CONTACT });
  if (phone) evidence.push({ type: EVIDENCE_TYPE.PHONE, value: phone, tier: IDENTITY_CONFIDENCE.CONTACT });
  if (name) evidence.push({ type: EVIDENCE_TYPE.NAME, value: name, tier: IDENTITY_CONFIDENCE.WEAK });
  return { clientId, sourceCustomerId, requesterUserId, email, phone, name, evidence, organizationId: str(record.organizationId) };
}

// ── canonical read-model key (identity precedence) ──────────────────────────────
// clientId > sourceCustomerId > requesterUserId > email > phone > name. A name-only key is LOW
// CONFIDENCE (namesakes are never silently merged into one strong identity).
export function clientKeyOf(record = {}) {
  const ev = normalizeClientEvidence(record);
  if (ev.clientId) return `client:${ev.clientId}`;
  if (ev.sourceCustomerId) return `src:${ev.sourceCustomerId}`;
  if (ev.requesterUserId) return `user:${ev.requesterUserId}`;
  if (ev.email) return `email:${ev.email}`;
  if (ev.phone) return `phone:${ev.phone}`;
  return ev.name ? `name:${ev.name}` : '';
}
export const isLowConfidenceKey = (k = '') => str(k).startsWith('name:');
export const keyTier = (k = '') => {
  const s = str(k);
  if (s.startsWith('client:') || s.startsWith('src:')) return IDENTITY_CONFIDENCE.EXPLICIT;
  if (s.startsWith('user:')) return IDENTITY_CONFIDENCE.STRONG;
  if (s.startsWith('email:') || s.startsWith('phone:')) return IDENTITY_CONFIDENCE.CONTACT;
  return IDENTITY_CONFIDENCE.WEAK;
};

// ── identity evidence index (organization-scoped) ───────────────────────────────
// Maps each strong/contact evidence VALUE to the set of explicit clientIds that carry it, so a new
// piece of evidence can resolve to an existing canonical client — or be seen as ambiguous/conflicting.
// Cross-tenant records are excluded when organizationId is supplied (identity never crosses orgs).
export function buildClientIdentityIndex(records = [], { organizationId = '' } = {}) {
  const org = str(organizationId);
  const byEvidence = new Map();      // `${type}:${value}` → Set(clientId)
  const clientEvidence = new Map();  // clientId → { emails:Set, phones:Set, userIds:Set }
  const add = (k, cid) => { if (!byEvidence.has(k)) byEvidence.set(k, new Set()); byEvidence.get(k).add(cid); };
  for (const r of records) {
    const rOrg = str(r.organizationId);
    if (org && rOrg && rOrg !== org) continue; // §33 tenant isolation
    const ev = normalizeClientEvidence(r);
    const cid = ev.clientId;
    if (!cid) continue; // only EXPLICIT clients anchor the index; weak records resolve against it
    if (!clientEvidence.has(cid)) clientEvidence.set(cid, { emails: new Set(), phones: new Set(), userIds: new Set() });
    const ce = clientEvidence.get(cid);
    if (ev.requesterUserId) { add(`${EVIDENCE_TYPE.USER_ID}:${ev.requesterUserId}`, cid); ce.userIds.add(ev.requesterUserId); }
    if (ev.email) { add(`${EVIDENCE_TYPE.EMAIL}:${ev.email}`, cid); ce.emails.add(ev.email); }
    if (ev.phone) { add(`${EVIDENCE_TYPE.PHONE}:${ev.phone}`, cid); ce.phones.add(ev.phone); }
    if (ev.sourceCustomerId) add(`${EVIDENCE_TYPE.SOURCE_CUSTOMER_ID}:${ev.sourceCustomerId}`, cid);
  }
  return { organizationId: org, byEvidence, clientEvidence };
}

// ── the resolution contract future adapters call ────────────────────────────────
// resolveClientIdentity(evidence, index) → { clientId, status, confidence, matchedBy, candidateIds, conflicts }
export function resolveClientIdentity(evidenceOrRecord, index = { byEvidence: new Map() }) {
  const ev = evidenceOrRecord && evidenceOrRecord.evidence ? evidenceOrRecord : normalizeClientEvidence(evidenceOrRecord || {});
  const R = (o) => ({ clientId: '', status: RESOLUTION_STATUS.UNRESOLVED, confidence: IDENTITY_CONFIDENCE.WEAK, matchedBy: '', candidateIds: [], conflicts: [], ...o });

  // 1) an explicit clientId (or trusted external customer id) IS the identity.
  if (ev.clientId) return R({ clientId: ev.clientId, status: RESOLUTION_STATUS.MATCHED, confidence: IDENTITY_CONFIDENCE.EXPLICIT, matchedBy: EVIDENCE_TYPE.CLIENT_ID, candidateIds: [ev.clientId] });

  const lookup = (type, value) => { const s = value && index.byEvidence.get(`${type}:${value}`); return s ? [...s] : []; };
  const srcCands = ev.sourceCustomerId ? lookup(EVIDENCE_TYPE.SOURCE_CUSTOMER_ID, ev.sourceCustomerId) : [];
  if (srcCands.length === 1) return R({ clientId: srcCands[0], status: RESOLUTION_STATUS.MATCHED, confidence: IDENTITY_CONFIDENCE.EXPLICIT, matchedBy: EVIDENCE_TYPE.SOURCE_CUSTOMER_ID, candidateIds: srcCands });
  if (srcCands.length > 1) return R({ status: RESOLUTION_STATUS.AMBIGUOUS, candidateIds: srcCands });

  const userCands = lookup(EVIDENCE_TYPE.USER_ID, ev.requesterUserId);
  const emailCands = lookup(EVIDENCE_TYPE.EMAIL, ev.email);
  const phoneCands = lookup(EVIDENCE_TYPE.PHONE, ev.phone);

  // §10 CROSS-EVIDENCE CONFLICT: two independent evidence types each resolve UNIQUELY but to
  // different canonical clients (email→A, phone→B). Fail closed — never auto-merge.
  const uniqueTargets = [userCands, emailCands, phoneCands].filter((a) => a.length === 1).map((a) => a[0]);
  if (new Set(uniqueTargets).size > 1) {
    return R({ status: RESOLUTION_STATUS.CONFLICT, candidateIds: [...new Set([...userCands, ...emailCands, ...phoneCands])], conflicts: [...new Set(uniqueTargets)] });
  }

  // STRONG: an authenticated userId that resolves uniquely.
  if (userCands.length === 1) return R({ clientId: userCands[0], status: RESOLUTION_STATUS.MATCHED, confidence: IDENTITY_CONFIDENCE.STRONG, matchedBy: EVIDENCE_TYPE.USER_ID, candidateIds: userCands });
  if (userCands.length > 1) return R({ status: RESOLUTION_STATUS.AMBIGUOUS, candidateIds: userCands });

  // CONTACT: exact email/phone. A single evidence value mapping to MULTIPLE clients is a shared/
  // household contact → AMBIGUOUS (§26). A unique contact match is CONTACT-confidence evidence.
  if (emailCands.length > 1 || phoneCands.length > 1) {
    return R({ status: RESOLUTION_STATUS.AMBIGUOUS, candidateIds: [...new Set([...emailCands, ...phoneCands])] });
  }
  const contactCands = [...new Set([...emailCands, ...phoneCands])];
  if (contactCands.length === 1) {
    const matchedBy = emailCands.length ? EVIDENCE_TYPE.EMAIL : EVIDENCE_TYPE.PHONE;
    return R({ clientId: contactCands[0], status: RESOLUTION_STATUS.MATCHED, confidence: IDENTITY_CONFIDENCE.CONTACT, matchedBy, candidateIds: contactCands });
  }
  if (contactCands.length > 1) return R({ status: RESOLUTION_STATUS.AMBIGUOUS, candidateIds: contactCands });

  // No existing client matched. If we hold strong/contact evidence, this is a resolvable NEW client
  // (a stable id can be minted). Name-only / empty stays UNRESOLVED (never a strong identity).
  if (ev.requesterUserId || ev.email || ev.phone || ev.sourceCustomerId) {
    return R({ status: RESOLUTION_STATUS.NEW, matchedBy: '', candidateIds: [] });
  }
  return R({ status: RESOLUTION_STATUS.UNRESOLVED });
}

// ── operator-selected existing client → stable clientId (the Pass-63 core fix) ───
// When an operator explicitly picks an "Existing client" in New Booking/Enquiry, that is an
// INTENTIONAL identity-resolution action (§13). Resolve it to a STABLE, REPRODUCIBLE clientId:
//   • already-canonical client:<id> or an explicit clientId → use it unchanged.
//   • strong/contact-keyed legacy client (user:/email:/phone:) → a DETERMINISTIC stable anchor,
//     so re-selecting the same historical client next season resolves to the SAME id (§15).
//   • name-only → NEVER anchored to a strong identity ('' → the case stays contact/name-derived).
export function resolveSelectedClientId(selectedClient = {}, organizationId = '') {
  const explicit = str(selectedClient.clientId);
  if (explicit) return { clientId: explicit, promoted: false, tier: IDENTITY_CONFIDENCE.EXPLICIT, anchorKey: `client:${explicit}` };
  const key = str(selectedClient.clientKey || clientKeyOf(selectedClient));
  if (!key) return { clientId: '', promoted: false, tier: null, anchorKey: '' };
  if (key.startsWith('client:')) return { clientId: key.slice('client:'.length), promoted: false, tier: IDENTITY_CONFIDENCE.EXPLICIT, anchorKey: key };
  if (key.startsWith('src:') || key.startsWith('user:') || key.startsWith('email:') || key.startsWith('phone:')) {
    return { clientId: stableClientId(organizationId, key), promoted: true, tier: keyTier(key), anchorKey: key };
  }
  return { clientId: '', promoted: false, tier: IDENTITY_CONFIDENCE.WEAK, anchorKey: key }; // name-only
}

// A brand-new lead (no existing evidence) → one stable clientId minted once and reused by every
// child booking of the case. Anchored to the case id so retry/materialisation cannot change it.
export function newLeadClientId(caseId) { return `client_${str(caseId)}`; }

// ── read-only identity audit (fingerprinted; NEVER emits raw email/phone) ────────
const fp = (v) => (v ? stableHashHex(str(v)).slice(0, 10) : '');
export function auditClientIdentity(records = [], { organizationId = '', isEligible = () => true } = {}) {
  const org = str(organizationId);
  const eligible = records.filter((r) => (!org || !str(r.organizationId) || str(r.organizationId) === org) && isEligible(r));
  const counts = { total: eligible.length, explicitClientId: 0, userIdOnly: 0, emailOnly: 0, phoneOnly: 0, nameOnly: 0, noIdentity: 0 };
  const emailToClients = new Map(); const phoneToClients = new Map(); const userToClients = new Map();
  const clientContacts = new Map(); // clientId → {emails,phones}
  for (const r of eligible) {
    const ev = normalizeClientEvidence(r);
    if (ev.clientId) counts.explicitClientId += 1;
    else if (ev.requesterUserId) counts.userIdOnly += 1;
    else if (ev.email) counts.emailOnly += 1;
    else if (ev.phone) counts.phoneOnly += 1;
    else if (ev.name) counts.nameOnly += 1;
    else counts.noIdentity += 1;
    const track = (map, val) => { if (!val) return; if (!map.has(val)) map.set(val, new Set()); if (ev.clientId) map.get(val).add(ev.clientId); };
    track(emailToClients, ev.email); track(phoneToClients, ev.phone); track(userToClients, ev.requesterUserId);
    if (ev.clientId) { if (!clientContacts.has(ev.clientId)) clientContacts.set(ev.clientId, { emails: new Set(), phones: new Set() }); const cc = clientContacts.get(ev.clientId); if (ev.email) cc.emails.add(ev.email); if (ev.phone) cc.phones.add(ev.phone); }
  }
  const multi = (map) => [...map.values()].filter((s) => s.size > 1).length;
  const conflicts = {
    emailToMultipleClients: multi(emailToClients),
    phoneToMultipleClients: multi(phoneToClients),
    userIdToMultipleClients: multi(userToClients),
    sharedContactClusters: [...emailToClients.values()].filter((s) => s.size > 1).length + [...phoneToClients.values()].filter((s) => s.size > 1).length,
  };
  // cross-evidence: a single record whose email→A(unique) and phone→B(unique) differ
  let crossEvidenceConflicts = 0;
  for (const r of eligible) {
    const ev = normalizeClientEvidence(r); if (ev.clientId) continue;
    const eSet = emailToClients.get(ev.email); const pSet = phoneToClients.get(ev.phone);
    if (eSet && pSet && eSet.size === 1 && pSet.size === 1 && [...eSet][0] !== [...pSet][0]) crossEvidenceConflicts += 1;
  }
  conflicts.crossEvidenceConflicts = crossEvidenceConflicts;
  // fingerprinted shared-contact samples (NO raw PII)
  const sharedSamples = [];
  for (const [val, set] of emailToClients) if (set.size > 1) sharedSamples.push({ type: 'email', fp: fp(val), clientCount: set.size });
  for (const [val, set] of phoneToClients) if (set.size > 1) sharedSamples.push({ type: 'phone', fp: fp(val), clientCount: set.size });
  return { organizationId: org, counts, conflicts, sharedSamples };
}
